import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { ModalEntryPoint } from '../shared/modal-entry-point';

import { DIALOG_CONFIG } from './dialog-config';
import { type PlanSelectionPageModalType } from './plan-selection-page-modal';
import { entryPoint } from './planSelectionPageModal.entrypoint';

const TWC_MODAL_CONFIG_DEFAULT = {
	width: '1116px',
	height: '733px',
};

const REVERSE_TRIAL_MODAL_CONFIG = {
	width: '968px',
	height: '740px',
};

const getModalConfig = (type: PlanSelectionPageModalType) => {
	if (type === 'reverse-trial') {
		return REVERSE_TRIAL_MODAL_CONFIG;
	}
	return TWC_MODAL_CONFIG_DEFAULT;
};

export function PlanSelectionPageModal({
	type,
}: {
	type: PlanSelectionPageModalType;
}): React.JSX.Element {
	const { cloudId, product, userId } = useEditionAwareness();
	const { formatMessage } = useIntl();

	const { width, height } = useMemo(() => getModalConfig(type), [type]);

	const shouldCheckContactSalesEligibility =
		type === 'reverse-trial' && Boolean(userId) && fg('twcg-1533-pls-contact-path-disco-gate');

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
			shouldCheckContactSalesEligibility,
			userId,
		}),
		[cloudId, product, shouldCheckContactSalesEligibility, userId],
	);

	const entryPointProps = useMemo(() => ({ type }), [type]);

	const modalProps = useMemo(
		() => ({
			width,
			height,
			shouldScrollInViewport: false,
			shouldCloseOnEscape: true,
			label: formatMessage({
				id: '@atlassian/edition-awareness.dialog.plan-selection-page.title',
				defaultMessage: 'Plan selection',
				description:
					'Accessible name for the plan selection dialog, announced to assistive technology users.',
			}),
		}),
		[width, height, formatMessage],
	);

	return (
		<ModalEntryPoint
			entryPointProps={entryPointProps}
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			modalProps={modalProps}
			dialogConfig={DIALOG_CONFIG}
		/>
	);
}
